export class TreasuryFundResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid Treasury fund ${operation} response: ${detail}`);
    this.name = 'TreasuryFundResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const text = value => typeof value === 'string' && value.length > 0;
const nullableText = value => value === null || typeof value === 'string';
const money = value => Number.isFinite(value) && value >= 0;
const date = value => value === null || typeof value === 'string' && Number.isFinite(Date.parse(value));
const visibility = value => ['internal', 'community', 'public'].includes(value);
const fail = (operation, detail) => { throw new TreasuryFundResponseContractError(operation, detail); };
const secretFree = value => !['password', 'passwordHash', 'accessToken', 'refreshToken', 'resetPasswordToken', 'resetPasswordExpires', 'emailVerificationCode', 'emailVerificationExpires'].some(key => Object.hasOwn(value, key));
const managerProjectionKeys = [
  'committedAmount', 'advanceOutstandingAmount', 'isOnchainTracked', 'observedOnchainBalance',
  'observedOnchainBalanceUpdatedAt', 'reconciliationReviewStatus', 'reconciliationReviewedAt',
  'reconciliationReviewNote', 'reconciliationReviewedBy', 'visibility', 'fundPolicy', 'walletCount',
];

const isDonationConfig = value => isRecord(value) && secretFree(value)
  && typeof value.isAcceptingContributions === 'boolean'
  && visibility(value.visibility)
  && Array.isArray(value.suggestedAmounts) && value.suggestedAmounts.every(amount => Number.isFinite(amount) && amount > 0)
  && nullableText(value.suggestedCurrency) && typeof value.instructions === 'string'
  && nullableText(value.contactEmail) && nullableText(value.externalUrl)
  && nullableText(value.referenceLabel) && nullableText(value.referenceHint);

const isFund = fund => isRecord(fund) && secretFree(fund)
  && ['id', 'code', 'fundName', 'currency'].every(key => text(fund[key]))
  && typeof fund.description === 'string' && money(fund.balance) && date(fund.lastUpdated)
  && ['fiat', 'crypto'].includes(fund.assetType)
  && nullableText(fund.chain) && nullableText(fund.tokenSymbol) && nullableText(fund.displaySymbol)
  && (fund.donationConfig === undefined || isDonationConfig(fund.donationConfig))
  && (fund.committedAmount === undefined || money(fund.committedAmount))
  && (fund.advanceOutstandingAmount === undefined || money(fund.advanceOutstandingAmount))
  && (fund.isOnchainTracked === undefined || typeof fund.isOnchainTracked === 'boolean')
  && (fund.observedOnchainBalance === undefined || fund.observedOnchainBalance === null || money(fund.observedOnchainBalance))
  && (fund.observedOnchainBalanceUpdatedAt === undefined || date(fund.observedOnchainBalanceUpdatedAt))
  && (fund.reconciliationReviewStatus === undefined || ['unreviewed', 'reviewed', 'reconciled'].includes(fund.reconciliationReviewStatus))
  && (fund.reconciliationReviewedAt === undefined || date(fund.reconciliationReviewedAt))
  && (fund.reconciliationReviewNote === undefined || nullableText(fund.reconciliationReviewNote))
  && (fund.reconciliationReviewedBy === undefined || fund.reconciliationReviewedBy === null
    || isRecord(fund.reconciliationReviewedBy) && secretFree(fund.reconciliationReviewedBy) && text(fund.reconciliationReviewedBy.id)
      && nullableText(fund.reconciliationReviewedBy.name) && nullableText(fund.reconciliationReviewedBy.email))
  && (fund.visibility === undefined || visibility(fund.visibility))
  && (fund.fundPolicy === undefined || isRecord(fund.fundPolicy) && secretFree(fund.fundPolicy)
    && typeof fund.fundPolicy.donationOnly === 'boolean' && typeof fund.fundPolicy.spendable === 'boolean'
    && typeof fund.fundPolicy.requiresApprovalForOutflow === 'boolean')
  && (fund.walletCount === undefined || Number.isInteger(fund.walletCount) && fund.walletCount >= 0)
  && (!managerProjectionKeys.some(key => key in fund)
    || managerProjectionKeys.every(key => key in fund) && 'donationConfig' in fund)
  && (managerProjectionKeys.some(key => key in fund) || fund.donationConfig === undefined
    || ['community', 'public'].includes(fund.donationConfig.visibility));

export const readTreasuryFundListResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !Array.isArray(payload.data) || !payload.data.every(isFund)) {
    fail('list', 'expected a scoped list of public or manager-visible fund records.');
  }
  return payload;
};

export const readTreasuryFundMutationResponse = payload => {
  if (!isRecord(payload) || payload.success !== true || !isFund(payload.data)
    || !text(payload.data.visibility) || !isRecord(payload.data.fundPolicy)
    || !isRecord(payload.data.donationConfig) || !Number.isInteger(payload.data.walletCount)) {
    fail('mutation', 'expected the saved manager-visible fund record.');
  }
  return payload;
};

export const readTreasuryFundPostingResponse = payload => {
  const posting = payload?.data;
  if (!isRecord(payload) || payload.success !== true || !isFund(posting)
    || !text(posting.visibility) || !isRecord(posting.fundPolicy)
    || !isRecord(posting.donationConfig) || !Number.isInteger(posting.walletCount)
    || !isRecord(posting.transaction) || !text(posting.transaction.id)
    || !Number.isFinite(posting.transaction.amount) || typeof posting.transaction.note !== 'string'
    || !date(posting.transaction.createdAt)
    || (posting.replayed !== undefined && typeof posting.replayed !== 'boolean')) {
    fail('posting', 'expected the saved manager-visible fund and ledger transaction.');
  }
  return payload;
};

export const isTreasuryFundRecord = isFund;
